import React, { useState, useEffect } from "react";
import { TextField } from "../TextField";
import { SelectDropdown } from "../SelectDropdown";
import { DateField } from "../DateField";
import { apiService } from "../../../services/api";
import {
    ComponentType,
    TextFieldComponent,
    SelectDropdownComponent,
    DateFieldComponent,
    ValidationRule,
    WidthMode,
    InputType,
} from "../../../services/types/ComponentTypes";
import {
    MenuResponse,
    MenuItem,
    ScreenPermission,
    DBUser,
    DBUserGroup,
    PermissionSettings
} from "../../../services/types/AccessRightsTypes";
import { 
    getNotificationPreferences, 
    saveNotificationPreferences,
    getAllTablePreferences,
    saveTablePreferences,
    getSubmoduleShowEditDelete,
    saveSubmoduleShowEditDelete,
    type NotificationPreferences,
    type TablePreferences
} from "../../../services/userPreferences";

interface SettingsPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SettingsPopup: React.FC<SettingsPopupProps> = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState<"business" | "access" | "notifications" | "views">("business");
    const [accessLevel, setAccessLevel] = useState<"server" | "group" | "user">("user");
    const [expandedAccordion, setExpandedAccordion] = useState<string | null>("0"); // Default open first one
    const [loading, setLoading] = useState(false);

    // Dynamic Options State
    const [countryOptions, setCountryOptions] = useState<any[]>([]);
    const [stateOptions, setStateOptions] = useState<any[]>([]);
    const [cityOptions, setCityOptions] = useState<any[]>([]);
    const [currencyOptions, setCurrencyOptions] = useState<any[]>([]);
    const [accountingYearOptions, setAccountingYearOptions] = useState<any[]>([]);

    const [formData, setFormData] = useState<Record<string, any>>({
        systemName: "",
        startingYear: "",
        phone: "",
        mobile: "",
        email: "",
        address: "",
        country: "",
        state: "",
        city: "",
        currency: "",
        accountingYear: "",
        trn: "",
        visaInterval: ""
    });

    const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
    const [selectedHeaderImage, setSelectedHeaderImage] = useState<File | null>(null);

    // --- Access Rights Logic ---

    // State for Access Rights
    const [menuData, setMenuData] = useState<MenuResponse | null>(null);
    const [screenPermissions, setScreenPermissions] = useState<ScreenPermission[]>([]);
    const [serverModulePermissions, setServerModulePermissions] = useState<Record<string, string>>({});
    const [groupModulePermissions, setGroupModulePermissions] = useState<Record<string, string>>({});
    const [userModulePermissions, setUserModulePermissions] = useState<Record<string, string>>({});

    // User/Group State
    const [userGroups, setUserGroups] = useState<DBUserGroup[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

    const [usersList, setUsersList] = useState<DBUser[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<DBUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<DBUser | null>(null);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState("");

    // Master Toggles
    const [toggleOperations, setToggleOperations] = useState(true);
    const [toggleAccounts, setToggleAccounts] = useState(true);
    const [toggleHR, setToggleHR] = useState(true);

    // Notification Preferences State
    const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences>(() => 
        getNotificationPreferences()
    );

    // View Preferences State
    const [viewPrefs, setViewPrefs] = useState<Record<string, TablePreferences>>(() => 
        getAllTablePreferences()
    );

    // Submodule Edit/Delete Preference State
    const [submoduleShowEditDelete, setSubmoduleShowEditDelete] = useState<boolean>(() =>
        getSubmoduleShowEditDelete()
    );

    // Notification Preference Visibility State (from API setting)
    const [showNotificationPreferences, setShowNotificationPreferences] = useState<boolean>(true);

    // Load notification preferences when component mounts or tab changes
    useEffect(() => {
        if (activeTab === "notifications") {
            setNotificationPrefs(getNotificationPreferences());
        } else if (activeTab === "views") {
            setViewPrefs(getAllTablePreferences());
            setSubmoduleShowEditDelete(getSubmoduleShowEditDelete());
        }
    }, [activeTab]);

    // Redirect to business tab if notification preferences are disabled and user is on notifications/views tab
    useEffect(() => {
        if (!showNotificationPreferences && (activeTab === "notifications" || activeTab === "views")) {
            setActiveTab("business");
        }
    }, [showNotificationPreferences, activeTab]);

    // Helper to format date from "11/20/2025 12:14:12 PM" to "YYYY-MM-DD"
    const formatDate = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toISOString().split('T')[0];
        } catch {
            return dateString;
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'header') => {
        if (e.target.files && e.target.files[0]) {
            if (type === 'logo') {
                setSelectedLogo(e.target.files[0]);
            } else {
                setSelectedHeaderImage(e.target.files[0]);
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchSettings();
            fetchInitialLookups();
        }
    }, [isOpen]);

    // Fetch States when Country changes
    useEffect(() => {
        if (formData.country) {
            fetchStates(formData.country);
        } else {
            setStateOptions([]);
            setCityOptions([]);
        }
    }, [formData.country]);

    // Fetch Cities when State changes
    useEffect(() => {
        if (formData.state) {
            fetchCities(formData.state);
        } else {
            setCityOptions([]);
        }
    }, [formData.state]);

    // Fetch initial data when Access tab opens
    useEffect(() => {
        if (isOpen && activeTab === "access") {
            if (accessLevel === "server") {
                fetchServerMenu();
            } else if (accessLevel === "group") {
                fetchUserGroups();
                if (selectedGroupId) fetchGroupMenu(selectedGroupId);
            } else if (accessLevel === "user") {
                fetchUsers();
                if (selectedUser) fetchUserMenu(selectedUser.id, selectedUser.role_id);
            }
        }
    }, [isOpen, activeTab, accessLevel]);

    const fetchInitialLookups = async () => {
        try {
            const [countriesRes, yearsRes]: [any, any] = await Promise.all([
                apiService.get("/pabws/api/get_countries"),
                apiService.get("/pabws/api/get_accounting_yeaars")
            ]);

            if (Array.isArray(countriesRes)) {
                setCountryOptions(countriesRes.map(c => ({ label: c.name, value: c.id })));

                // Map Currency to Country ID as per user requirement (Currency ID = Country ID)
                // Showing ONLY currency code as label as per specific user request
                // This may result in duplicate labels (e.g. multiple "EUR"), but ensures exact ID mapping
                const currencyOpts = countriesRes
                    .filter(c => c.currency) // Only countries with currency
                    .map(c => ({
                        label: c.currency,
                        value: c.id
                    }));
                setCurrencyOptions(currencyOpts);
            }
            if (Array.isArray(yearsRes)) {
                setAccountingYearOptions(yearsRes.map(y => ({ label: y.name, value: y.id })));
            }
        } catch (error) {
            console.error("Failed to fetch initial lookups", error);
        }
    };

    const fetchStates = async (countryId: string | number) => {
        if (!countryId) return;
        try {
            const statesRes: any = await apiService.get(`/pabws/api/get_states?country_id=${countryId}`);
            if (Array.isArray(statesRes)) {
                setStateOptions(statesRes.map(s => ({ label: s.name, value: s.id })));
            }
        } catch (error) {
            console.error("Failed to fetch states", error);
            setStateOptions([]);
        }
    };

    const fetchCities = async (stateId: string | number) => {
        if (!stateId) return;
        try {
            const citiesRes: any = await apiService.get(`/pabws/api/get_cities?state_id=${stateId}`);
            if (Array.isArray(citiesRes)) {
                setCityOptions(citiesRes.map(c => ({ label: c.name, value: c.id })));
            }
        } catch (error) {
            console.error("Failed to fetch cities", error);
            setCityOptions([]);
        }
    };

    const fetchSettings = async () => {
        //setLoading(true);
        try {
            const response: any = await apiService.get("/pabws/api/getsettings");
            if (response && response.setting) {
                const s = response.setting;
                setFormData({
                    systemName: s.system_name || "",
                    startingYear: s.starting_year ? formatDate(s.starting_year) : "",
                    phone: s.phone || "",
                    mobile: s.mobile || "",
                    email: s.email || "",
                    address: s.address || "",
                    country: s.country || "",
                    state: s.state || "",
                    city: s.city || "",
                    currency: s.currency || "",
                    accountingYear: s.accounting_year_id || "",
                    trn: s.TRN || "",
                    visaInterval: s.visa_expiration_reminder_interval || "",
                    logo: s.logo,
                    headerImage: s.header_image
                });
                // Extract NotificatiionPreference setting (note: typo in API response)
                setShowNotificationPreferences(s.NotificatiionPreference === true || s.NotificatiionPreference === "true");
            }
        } catch (error) {
            console.error("Failed to fetch settings:", error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const handleChange = (name: string, value: any) => {
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            // Cascading resets
            if (name === "country") {
                newData.state = "";
                newData.city = "";
            }
            if (name === "state") {
                newData.city = "";
            }

            return newData;
        });
    };

    const toggleAccordion = (id: string) => {
        setExpandedAccordion(expandedAccordion === id ? null : id);
    };

    // --- Configuration Objects for Fields ---

    const systemNameConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "systemName",
        label: "System name",
        required: true,
        validation: [{ rule: ValidationRule.REQUIRED }],
        style: { widthMode: WidthMode.FULL }
    };

    const startingYearConfig: DateFieldComponent = {
        componentType: ComponentType.DATE_FIELD,
        name: "startingYear",
        label: "Starting year",
        style: { widthMode: WidthMode.FULL }
    };

    const phoneConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "phone",
        label: "Phone",
        inputType: InputType.PHONE,
        style: { widthMode: WidthMode.FULL }
    };

    const mobileConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "mobile",
        label: "Mobile",
        required: true,
        validation: [{ rule: ValidationRule.REQUIRED }],
        inputType: InputType.PHONE,
        style: { widthMode: WidthMode.FULL }
    };

    const emailConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "email",
        label: "Email",
        required: true,
        validation: [{ rule: ValidationRule.REQUIRED }, { rule: ValidationRule.EMAIL }],
        inputType: InputType.EMAIL,
        style: { widthMode: WidthMode.FULL }
    };

    const addressConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "address",
        label: "Address",
        style: { widthMode: WidthMode.FULL }
    };

    const countryConfig: SelectDropdownComponent = {
        componentType: ComponentType.SELECT_DROPDOWN,
        name: "country",
        label: "Country",
        options: countryOptions, // Dynamic Options
        style: { widthMode: WidthMode.FULL }
    };

    const stateConfig: SelectDropdownComponent = {
        componentType: ComponentType.SELECT_DROPDOWN,
        name: "state",
        label: "State",
        options: stateOptions, // Dynamic Options
        style: { widthMode: WidthMode.FULL }
    };

    const cityConfig: SelectDropdownComponent = {
        componentType: ComponentType.SELECT_DROPDOWN,
        name: "city",
        label: "City",
        options: cityOptions, // Dynamic Options
        style: { widthMode: WidthMode.FULL }
    };

    const currencyConfig: SelectDropdownComponent = {
        componentType: ComponentType.SELECT_DROPDOWN,
        name: "currency",
        label: "Currency",
        options: currencyOptions, // Dynamic Options
        style: { widthMode: WidthMode.FULL }
    };

    const accountingYearConfig: SelectDropdownComponent = {
        componentType: ComponentType.SELECT_DROPDOWN,
        name: "accountingYear",
        label: "Accounting year",
        options: accountingYearOptions, // Dynamic Options
        style: { widthMode: WidthMode.FULL }
    };

    const trnConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "trn",
        label: "TRN",
        style: { widthMode: WidthMode.FULL }
    };

    const visaIntervalConfig: TextFieldComponent = {
        componentType: ComponentType.TEXT_FIELD,
        name: "visaInterval",
        label: "Visa expiration reminder interval",
        style: { widthMode: WidthMode.FULL }
    };

    // --- Access Rights Logic ---

    const fetchServerMenu = async () => {
        //setLoading(true);
        try {
            const res: any = await apiService.get("/pabws/menu");
            if (res && res.data) {
                setMenuData(res);
                // Clear all tracking states
                setScreenPermissions([]);
                setServerModulePermissions({});
                setGroupModulePermissions({});
                setUserModulePermissions({});
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserGroups = async () => {
        try {
            const res: any = await apiService.get("/pabws/api/get_user_groups");
            if (Array.isArray(res)) {
                setUserGroups(res);
                if (!selectedGroupId && res.length > 0) {
                    setSelectedGroupId(res[0].id);
                    fetchGroupMenu(res[0].id);
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchGroupMenu = async (groupId: number) => {
        //setLoading(true);
        try {
            const res: any = await apiService.get(`/pabws/menu_with_server_permission?group_id=${groupId}`);
            if (res && res.data) {
                setMenuData(res);
                setScreenPermissions([]);
                setServerModulePermissions({});
                setGroupModulePermissions({});
                setUserModulePermissions({});
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res: any = await apiService.get("/pabws/api/getusers");
            if (Array.isArray(res)) {
                setUsersList(res);
                setFilteredUsers(res);
                if (!selectedUser && res.length > 0) {
                    handleUserSelect(res[0]);
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleUserSelect = (user: DBUser) => {
        setSelectedUser(user);
        setUserSearchTerm(user.username);
        setIsUserDropdownOpen(false);
        fetchUserMenu(user.id, user.role_id);
    };

    const fetchUserMenu = async (userId: string, roleId: number) => {
        //setLoading(true);
        try {
            const res: any = await apiService.get(`/pabws/menu_with_user_level_permission/${roleId}/${userId}`);
            if (res && res.data) {
                setMenuData(res);
                setScreenPermissions([]);
                setServerModulePermissions({});
                setGroupModulePermissions({});
                setUserModulePermissions({});
                syncToggleStates(res.data); // Sync master toggles
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // --- Logic for Toggles ---
    const MODULE_GROUPS = {
        operations: ["Garage operations", "Inventory Management", "Sales", "Purchases", "Master data manager", "Reports"],
        accounts: ["Accounts management", "Vouchers", "Assets & Liabilities"],
        hr: ["Employee management"]
    };

    const syncToggleStates = (data: MenuItem[]) => {
        const getStatus = (modules: string[]) => {
            const mods = data.filter(item => {
                const sub = item.SubMenu[0];
                return sub && modules.includes(sub.Module_Name || "");
            });
            if (mods.length === 0) return true;

            return mods.every(item => {
                const sub = item.SubMenu[0];
                if (accessLevel === "server") return (sub.ServerLevelPermission ?? 0) === 0;
                if (accessLevel === "group") return (sub.GroupLevelPermission ?? 0) === 0;
                return (sub.UserLevelPermission ?? 0) === 0;
            });
        };

        setToggleOperations(getStatus(MODULE_GROUPS.operations));
        setToggleAccounts(getStatus(MODULE_GROUPS.accounts));
        setToggleHR(getStatus(MODULE_GROUPS.hr));
    };

    const handleMasterToggle = (category: "operations" | "accounts" | "hr", newValue: boolean) => {
        if (category === "operations") setToggleOperations(newValue);
        else if (category === "accounts") setToggleAccounts(newValue);
        else if (category === "hr") setToggleHR(newValue);

        if (!menuData || !menuData.data) return;

        const updatedData = [...menuData.data];
        const newPermissionValue = newValue ? 0 : 1; // 0 = Enabled
        const modulesToUpdate = MODULE_GROUPS[category];

        updatedData.forEach(item => {
            const sub = item.SubMenu[0];
            if (sub && modulesToUpdate.includes(sub.Module_Name || "")) {
                const currentVal = accessLevel === "server" ? (sub.ServerLevelPermission ?? 0) :
                    accessLevel === "group" ? (sub.GroupLevelPermission ?? 0) : (sub.UserLevelPermission ?? 0);

                if (currentVal !== newPermissionValue) {
                    if (accessLevel === "server") {
                        sub.ServerLevelPermission = newPermissionValue;
                        setServerModulePermissions(prev => ({ ...prev, [sub.Module_Id]: newPermissionValue.toString() }));
                    } else if (accessLevel === "group") {
                        sub.GroupLevelPermission = newPermissionValue;
                        setGroupModulePermissions(prev => ({ ...prev, [sub.Module_Id]: newPermissionValue.toString() }));
                    } else {
                        sub.UserLevelPermission = newPermissionValue;
                        setUserModulePermissions(prev => ({ ...prev, [sub.Module_Id]: newPermissionValue.toString() }));
                    }
                }
            }
        });
        setMenuData({ ...menuData, data: updatedData });
    };

    const handleScreenPermissionChange = (menu: MenuItem, type: 'Add' | 'Edit' | 'Delete' | 'Unlock' | 'View') => {
        const updatedPermission = { ...menu.Permission, [type]: !menu.Permission[type] };
        menu.Permission = updatedPermission; // Mutating for local state reflection immediate update (simpler for deep nesting)

        // Update tracking array
        const existingIdx = screenPermissions.findIndex(x => x.ScreenId === menu.Id);
        const newEntry: ScreenPermission = {
            ScreenId: menu.Id,
            ScreenSettings: updatedPermission,
            ScreenVisibility: accessLevel === "server" ? (menu.ServerLevelScreenVisibility ?? 0) :
                accessLevel === "group" ? (menu.GroupLevelScreenVisibility ?? 0) : (menu.UserLevelScreenVisibility ?? 0)
        };

        const newPermissions = [...screenPermissions];
        if (existingIdx >= 0) {
            newPermissions[existingIdx] = newEntry;
        } else {
            newPermissions.push(newEntry);
        }
        setScreenPermissions(newPermissions);
        setMenuData({ ...menuData! }); // Force re-render
    };

    const handleScreenVisibilityChange = (menu: MenuItem) => {
        let currentVisibility = 0;
        if (accessLevel === "server") {
            menu.ServerLevelScreenVisibility = (menu.ServerLevelScreenVisibility ?? 0) === 0 ? 1 : 0;
            currentVisibility = menu.ServerLevelScreenVisibility;
        } else if (accessLevel === "group") {
            menu.GroupLevelScreenVisibility = (menu.GroupLevelScreenVisibility ?? 0) === 0 ? 1 : 0;
            currentVisibility = menu.GroupLevelScreenVisibility;
        } else {
            menu.UserLevelScreenVisibility = (menu.UserLevelScreenVisibility ?? 0) === 0 ? 1 : 0;
            currentVisibility = menu.UserLevelScreenVisibility;
        }

        // Update tracking
        const existingIdx = screenPermissions.findIndex(x => x.ScreenId === menu.Id);
        const newEntry: ScreenPermission = {
            ScreenId: menu.Id,
            ScreenSettings: menu.Permission,
            ScreenVisibility: currentVisibility
        };
        const newPermissions = [...screenPermissions];
        if (existingIdx >= 0) {
            newPermissions[existingIdx] = newEntry;
        } else {
            newPermissions.push(newEntry);
        }
        setScreenPermissions(newPermissions);
        setMenuData({ ...menuData! }); // Force re-render
    };

    const handleModuleToggle = (itm: MenuItem) => {
        const subMenuFirst = itm.SubMenu[0];
        if (!subMenuFirst) return;

        if (accessLevel === "server") {
            const currentVal = subMenuFirst.ServerLevelPermission ?? 0;
            const newVal = currentVal === 0 ? 1 : 0;
            subMenuFirst.ServerLevelPermission = newVal;
            setServerModulePermissions(prev => ({ ...prev, [subMenuFirst.Module_Id]: newVal.toString() }));
        } else if (accessLevel === "group") {
            const currentVal = subMenuFirst.GroupLevelPermission ?? 0;
            const newVal = currentVal === 0 ? 1 : 0;
            subMenuFirst.GroupLevelPermission = newVal;
            setGroupModulePermissions(prev => ({ ...prev, [subMenuFirst.Module_Id]: newVal.toString() }));
        } else {
            // User Level
            const currentVal = subMenuFirst.UserLevelPermission ?? 0;
            const newVal = currentVal === 0 ? 1 : 0;
            subMenuFirst.UserLevelPermission = newVal;
            setUserModulePermissions(prev => ({ ...prev, [subMenuFirst.Module_Id]: newVal.toString() }));
        }
        setMenuData({ ...menuData! });
        syncToggleStates(menuData!.data);
    };

    const handleAccessSubmit = async () => {
        setLoading(true);
        try {
            if (accessLevel === "server") {
                const payload = {
                    permission_data: serverModulePermissions,
                    screenPermission: screenPermissions
                };
                await apiService.post("/pabws/api/add_serverlevel_module_permission", payload);
            } else if (accessLevel === "group") {
                const payload = {
                    permission_data: groupModulePermissions,
                    screenPermission: screenPermissions
                };
                // API endpoint for group
                await apiService.post(`/pabws/api/add_group_level_module_permission/${selectedGroupId}`, payload);
            } else if (accessLevel === "user") {
                const payload = {
                    permission_data: userModulePermissions,
                    screenPermission: screenPermissions
                };
                await apiService.post(`/pabws/api/add_user_level_module_permission/${selectedUser?.id}`, payload);
            }
            // alert("Permissions saved!");
        } catch (err) {
            console.error("Failed to save permissions", err);
        } finally {
            setLoading(false);
            // Refresh data
            if (accessLevel === "server") fetchServerMenu();
            else if (accessLevel === "group" && selectedGroupId) fetchGroupMenu(selectedGroupId);
            else if (accessLevel === "user" && selectedUser) fetchUserMenu(selectedUser.id, selectedUser.role_id);
        }
    };

    const handleNotificationPreferencesSubmit = async () => {
        setLoading(true);
        try {
            saveNotificationPreferences(notificationPrefs);
            // Preferences are automatically synced to backend via saveUserPreferences
            if (import.meta.env.DEV) {
                console.log("[Settings] Notification preferences saved:", notificationPrefs);
            }
        } catch (err) {
            console.error("Failed to save notification preferences", err);
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationToggle = (key: keyof NotificationPreferences) => {
        setNotificationPrefs(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleViewModeChange = (tableId: string, viewMode: "table" | "card") => {
        setViewPrefs(prev => ({
            ...prev,
            [tableId]: {
                ...prev[tableId],
                viewMode,
                columnVisibility: prev[tableId]?.columnVisibility || {},
                columnPinning: prev[tableId]?.columnPinning || {},
                tableViewMode: prev[tableId]?.tableViewMode,
            }
        }));
    };

    const handleTableViewModeChange = (tableId: string, tableViewMode: "progress" | "standard") => {
        setViewPrefs(prev => ({
            ...prev,
            [tableId]: {
                ...prev[tableId],
                tableViewMode,
                columnVisibility: prev[tableId]?.columnVisibility || {},
                columnPinning: prev[tableId]?.columnPinning || {},
                viewMode: prev[tableId]?.viewMode,
            }
        }));
    };

    const handleViewPreferencesSubmit = async () => {
        setLoading(true);
        try {
            // Save all view preferences
            Object.entries(viewPrefs).forEach(([tableId, prefs]) => {
                saveTablePreferences(tableId, prefs);
            });
            // Save submodule Edit/Delete preference
            saveSubmoduleShowEditDelete(submoduleShowEditDelete);
            if (import.meta.env.DEV) {
                console.log("[Settings] View preferences saved:", viewPrefs);
                console.log("[Settings] Submodule Edit/Delete preference saved:", submoduleShowEditDelete);
            }
        } catch (err) {
            console.error("Failed to save view preferences", err);
        } finally {
            setLoading(false);
        }
    };

    // Helper to check if a module is enabled based on access level
    const isModuleEnabled = (item: MenuItem) => {
        const sub = item.SubMenu[0];
        if (!sub) return false;
        if (accessLevel === "server") return (sub.ServerLevelPermission ?? 0) === 0;
        if (accessLevel === "group") return (sub.GroupLevelPermission ?? 0) === 0;
        if (accessLevel === "user") return (sub.UserLevelPermission ?? 0) === 0;
        return false;
    };

    const renderAccessContent = () => {
        if (!menuData || !menuData.data) return <div className="p-4 text-center text-gray-700 dark:text-gray-300">Loading permissions...</div>;

        return (
            <div className="flex flex-col h-full">
                {/* Header Section for User/Group Selection */}
                {accessLevel === "group" && (
                    <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Select Group</label>
                        <select
                            className="w-full border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 dark:text-gray-300 p-1.5"
                            value={selectedGroupId || ""}
                            onChange={(e) => {
                                const gid = Number(e.target.value);
                                setSelectedGroupId(gid);
                                fetchGroupMenu(gid);
                            }}
                        >
                            {userGroups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {accessLevel === "user" && (
                    <div className="mb-2 relative">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Select User</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg text-sm p-1.5 bg-gray-50 dark:bg-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="Search users..."
                            value={userSearchTerm}
                            onChange={(e) => {
                                setUserSearchTerm(e.target.value);
                                setIsUserDropdownOpen(true);
                                setFilteredUsers(usersList.filter(u => u.username?.toLowerCase().includes(e.target.value.toLowerCase())));
                            }}
                            onFocus={() => setIsUserDropdownOpen(true)}
                        />
                        {isUserDropdownOpen && filteredUsers.length > 0 && (
                            <div className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                                {filteredUsers.map(u => (
                                    <div
                                        key={u.id}
                                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-300"
                                        onClick={() => handleUserSelect(u)}
                                    >
                                        {u.username}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Master Toggles Section */}
                <div className="mb-3 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 m-0">Master Toggles</h3>
                        <div className="flex items-center gap-6">
                            <div className="flex flex-col items-center gap-1">
                                <input
                                    type="checkbox"
                                    className="toggle-switch"
                                    checked={toggleOperations}
                                    onChange={(e) => handleMasterToggle("operations", e.target.checked)}
                                />
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Operations modules</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <input
                                    type="checkbox"
                                    className="toggle-switch"
                                    checked={toggleAccounts}
                                    onChange={(e) => handleMasterToggle("accounts", e.target.checked)}
                                />
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Accounts module</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <input
                                    type="checkbox"
                                    className="toggle-switch"
                                    checked={toggleHR}
                                    onChange={(e) => handleMasterToggle("hr", e.target.checked)}
                                />
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Hr module</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Accordions */}
                <div className="flex-1 overflow-y-auto pr-1 space-y-1.5">
                    {menuData.data.map((item, idx) => {
                        if (!item.SubMenu || item.SubMenu.length === 0) return null;
                        const firstSub = item.SubMenu[0];
                        const isExpanded = expandedAccordion === idx.toString();
                        const isEnabled = isModuleEnabled(item);

                        return (
                            <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700">
                                    <button
                                        onClick={() => setExpandedAccordion(isExpanded ? null : idx.toString())}
                                        className="flex items-center gap-1.5 font-semibold text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        {firstSub.Module_Name}
                                        <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    <div className="flex items-center gap-1.5">
                                        <input
                                            type="checkbox"
                                            className="toggle-switch transform scale-90"
                                            checked={isEnabled}
                                            onChange={() => handleModuleToggle(item)}
                                        />
                                    </div>
                                </div>

                                {isExpanded && isEnabled && (
                                    <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                                        <div className="mb-1.5 flex items-center gap-1.5">
                                            <svg width="16" height="16" viewBox="0 0 18 18" fill="none" className="text-gray-800 dark:text-gray-200"><path d="M5.25 16.5H12.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M1.5 12.75V3C1.5 2.6 1.6 2.2 1.9 1.9 2.2 1.6 2.6 1.5 3 1.5H15C15.4 1.5 15.8 1.6 16 1.9C16.3 2.2 16.5 2.6 16.5 3V12.75C16.5 13.1 16.3 13.5 16 13.8C15.8 14.1 15.4 14.25 15 14.25H3C2.6 14.25 2.2 14.1 1.9 13.8C1.6 13.5 1.5 13.1 1.5 12.75Z" stroke="currentColor" strokeWidth="1.5" /><path d="M6.75 7.875L8.25 9.375L11.25 6.375" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            <span className="font-bold text-xs text-gray-800 dark:text-gray-200">Screens</span>
                                        </div>

                                        {item.SubMenu.map((sub, sIdx) => {
                                            const hidden = accessLevel === "server" ? (sub.ServerLevelScreenVisibility ?? 0) === 1 :
                                                accessLevel === "group" ? (sub.GroupLevelScreenVisibility ?? 0) === 1 :
                                                    (sub.UserLevelScreenVisibility ?? 0) === 1;

                                            return (
                                                <div key={sIdx} className="border border-gray-100 dark:border-gray-700 rounded p-1.5 mb-1.5 flex items-center justify-between bg-white dark:bg-gray-800">
                                                    <div className="flex items-center gap-2 w-1/3">
                                                        <button onClick={() => handleScreenVisibilityChange(sub)} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
                                                            {hidden ? <i className="bi bi-eye-slash text-red-500"></i> : <i className="bi bi-eye text-primary-600 dark:text-primary-400"></i>}
                                                        </button>
                                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{sub.Label}</span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5 w-2/3 justify-end">
                                                        {(["Add", "Edit", "Delete", "Unlock"] as const).map(action => {
                                                            const initialPerm = sub.InItialPermission && (sub.InItialPermission as any)[action];
                                                            const currentPerm = sub.Permission && (sub.Permission as any)[action];

                                                            // If not visible, disable everything
                                                            // If initial permission is false, disable
                                                            const disabled = hidden || !initialPerm;

                                                            return (
                                                                <div key={action} className={`flex items-center justify-between border rounded px-1.5 py-0.5 min-w-[80px] ${disabled ? "bg-gray-50 dark:bg-gray-700 opacity-60" : "bg-white dark:bg-gray-800"} border-gray-200 dark:border-gray-700`}>
                                                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{action}</span>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="toggle-switch transform scale-75 origin-right"
                                                                        checked={!disabled && currentPerm}
                                                                        disabled={disabled}
                                                                        onChange={() => handleScreenPermissionChange(sub, action)}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };


    const formatDateForApi = (dateString: string): string => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const now = new Date();
            date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            const yyyy = date.getFullYear();

            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';

            hours = hours % 12;
            hours = hours ? hours : 12;

            return `${mm}/${dd}/${yyyy} ${hours}:${minutes}:${seconds} ${ampm}`;
        } catch (e) {
            console.error("Date formatting error:", e);
            return dateString;
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            let logoBase64 = "login-icon.png";
            let headerImageBase64 = "header-image.png";

            if (selectedLogo) {
                const base64 = await fileToBase64(selectedLogo);
                // Remove header like "data:image/png;base64,"
                logoBase64 = base64.replace(/^data:image\/[a-z]+;base64,/, "");
            }

            if (selectedHeaderImage) {
                const base64 = await fileToBase64(selectedHeaderImage);
                headerImageBase64 = base64.replace(/^data:image\/[a-z]+;base64,/, "");
            }

            const payload = {
                starting_year: formatDateForApi(formData.startingYear),
                phone: formData.phone,
                mobile: formData.mobile,
                email: formData.email,
                address: formData.address,
                country: formData.country,
                state: formData.state,
                city: formData.city,
                logo: logoBase64,
                header_image: headerImageBase64,
                system_name: formData.systemName,
                currency: formData.currency,
                accounting_year_id: formData.accountingYear,
                TRN: formData.trn,
                visa_expiration_reminder_interval: Number(formData.visaInterval) || 14
            };

            await apiService.request({
                method: "POST",
                endpoint: "/pabws/api/addsettings",
                payload: payload
            });

            // alert("Settings saved successfully!");
            //  onClose();
        } catch (error) {
            console.error("Failed to save settings:", error);
            // alert("Failed to save settings.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1002] flex items-center justify-center p-2">
            {/* Styles from the provided HTML for custom overrides if needed */}
            <style>{`
                .custom-file-input::-webkit-file-upload-button {
                    background: #e9ecef;
                    border: none;
                    border-right: 1px solid #ced4da;
                    color: #495057;
                    padding: 0.375rem 0.75rem;
                    margin-right: 0.75rem;
                    height: 100%;
                }
                .dark .custom-file-input::-webkit-file-upload-button {
                    background: #4b5563;
                    border-right-color: #6b7280;
                    color: #e5e7eb;
                }
                .toggle-switch {
                    appearance: none !important;
                    width: 2.5em;
                    height: 1.25em;
                    background: #dee2e6 !important;
                    background-color: #dee2e6 !important;
                    border-radius: 2em;
                    position: relative;
                    cursor: pointer;
                    outline: none;
                    transition: background-color .2s ease-in-out;
                }
                .dark .toggle-switch {
                    background: #4b5563 !important;
                    background-color: #4b5563 !important;
                }
                .toggle-switch::after {
                    content: '';
                    position: absolute;
                    top: 0.125em;
                    left: 0.125em;
                    width: 1em;
                    height: 1em;
                    background-color: white !important;
                    border-radius: 50%;
                    transition: transform .2s ease-in-out;
                }
                .dark .toggle-switch::after {
                    background-color: #e5e7eb !important;
                }
                .toggle-switch:checked {
                    background: var(--color-primary-500, #35ba5b) !important;
                    background-color: var(--color-primary-500, #35ba5b) !important;
                }
                .toggle-switch:checked::after {
                    transform: translateX(1.25em);
                }
                .dark .toggle-switch:checked {
                    background: var(--color-primary-500, #35ba5b) !important;
                    background-color: var(--color-primary-500, #35ba5b) !important;
                }
                .toggle-switch:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `}</style>

            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white dark:bg-gray-800 w-full max-w-7xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">

                {/* Header */}
                <div className="p-3 px-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800 shrink-0">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 m-0">Settings</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-1 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Main Tabs */}
                <div className="px-4 pt-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => {
                                setActiveTab("business");
                                // Focus first searchable dropdown input after tab switch
                                setTimeout(() => {
                                    const firstSearchableDropdownInput = document.querySelector('[data-searchable-dropdown-input="true"]:not([disabled])') as HTMLInputElement;
                                    if (firstSearchableDropdownInput) {
                                        firstSearchableDropdownInput.focus();
                                    }
                                }, 100);
                            }}
                            className={`pb-2 text-base font-medium transition-colors ${activeTab === "business"
                                ? "text-gray-900 dark:text-gray-100"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                }`}
                        >
                            Business information
                        </button>

                        {/* Fixed Primary Bar Separator */}
                        <div className="h-5 w-1 bg-primary-500 dark:bg-primary-400 rounded-sm mb-2"></div>

                        <button
                            onClick={() => {
                                setActiveTab("access");
                                // Focus first searchable dropdown input after tab switch
                                setTimeout(() => {
                                    const firstSearchableDropdownInput = document.querySelector('[data-searchable-dropdown-input="true"]:not([disabled])') as HTMLInputElement;
                                    if (firstSearchableDropdownInput) {
                                        firstSearchableDropdownInput.focus();
                                    }
                                }, 100);
                            }}
                            className={`pb-2 text-base font-medium transition-colors ${activeTab === "access"
                                ? "text-gray-900 dark:text-gray-100"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                }`}
                        >
                            Access rights
                        </button>

                        {/* Notification Preferences Tab - Only show if enabled */}
                        {showNotificationPreferences && (
                            <>
                                {/* Fixed Primary Bar Separator */}
                                <div className="h-5 w-1 bg-primary-500 dark:bg-primary-400 rounded-sm mb-2"></div>

                                <button
                                    onClick={() => {
                                        setActiveTab("notifications");
                                    }}
                                    className={`pb-2 text-base font-medium transition-colors ${activeTab === "notifications"
                                        ? "text-gray-900 dark:text-gray-100"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                        }`}
                                >
                                    Notification preferences
                                </button>
                            </>
                        )}

                        {/* View Preferences Tab - Only show if notification preferences are enabled */}
                        {showNotificationPreferences && (
                            <>
                                {/* Fixed Primary Bar Separator */}
                                <div className="h-5 w-1 bg-primary-500 dark:bg-primary-400 rounded-sm mb-2"></div>

                                <button
                                    onClick={() => {
                                        setActiveTab("views");
                                    }}
                                    className={`pb-2 text-base font-medium transition-colors ${activeTab === "views"
                                        ? "text-gray-900 dark:text-gray-100"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                        }`}
                                >
                                    View preferences
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="p-4 overflow-y-auto custom-scrollbar flex-1">
                    {/* Inner Card */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm min-h-[600px]">

                        {activeTab === "business" ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 dark:text-gray-100">
                                {/* Using smaller gap-y-4 for tighter vertical alignment */}

                                <TextField config={systemNameConfig} value={formData.systemName} onChange={handleChange} />
                                <DateField config={startingYearConfig} value={formData.startingYear} onChange={handleChange} />
                                <TextField config={phoneConfig} value={formData.phone} onChange={handleChange} />

                                <TextField config={mobileConfig} value={formData.mobile} onChange={handleChange} />
                                <TextField config={emailConfig} value={formData.email} onChange={handleChange} />
                                <TextField config={addressConfig} value={formData.address} onChange={handleChange} />

                                <SelectDropdown config={countryConfig} value={formData.country} onChange={handleChange} />
                                <SelectDropdown config={stateConfig} value={formData.state} onChange={handleChange} />
                                <SelectDropdown config={cityConfig} value={formData.city} onChange={handleChange} />

                                {/* Custom File Inputs to match the "Small One" screenshot */}
                                <div className="flex flex-col gap-0.5 w-full">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Logo</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, 'logo')}
                                        className="block w-full text-sm text-gray-500 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 focus:outline-none custom-file-input"
                                    />
                                </div>

                                <div className="flex flex-col gap-0.5 w-full">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Invoice header image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, 'header')}
                                        className="block w-full text-sm text-gray-500 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 focus:outline-none custom-file-input"
                                    />
                                </div>

                                <SelectDropdown config={currencyConfig} value={formData.currency} onChange={handleChange} />

                                <SelectDropdown config={accountingYearConfig} value={formData.accountingYear} onChange={handleChange} />
                                <TextField config={trnConfig} value={formData.trn} onChange={handleChange} />
                                <TextField config={visaIntervalConfig} value={formData.visaInterval} onChange={handleChange} />
                            </div>
                        ) : activeTab === "access" ? (
                            /* Access Rights Content */
                            <div className="flex flex-col h-full">
                                <div className="flex justify-between items-center mb-3">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Access permissions</h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Manage access permissions for users, groups, and servers</p>
                                    </div>
                                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                        <span className="mr-1">Home</span> / <span className="ml-1 font-semibold">Permissions</span>
                                    </div>
                                </div>

                                {/* Access Level Tabs */}
                                <div className="bg-gray-100 dark:bg-gray-700 p-1 rounded-t-lg flex mb-3">
                                    {(["server", "group", "user"] as const).map((level) => (
                                        <button
                                            key={level}
                                            onClick={() => { setAccessLevel(level); setMenuData(null); }}
                                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${accessLevel === level
                                                ? "bg-success-500 text-white shadow-sm"
                                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                                }`}
                                        >
                                            {/* Icons simplified */}
                                            {level === "server" && <i className="bi bi-server"></i>}
                                            {level === "group" && <i className="bi bi-people"></i>}
                                            {level === "user" && <i className="bi bi-person"></i>}
                                            {level.charAt(0).toUpperCase() + level.slice(1)} level
                                        </button>
                                    ))}
                                </div>

                                {/* Dynamic Content based on API Data */}
                                {renderAccessContent()}
                            </div>
                        ) : activeTab === "notifications" && showNotificationPreferences ? (
                            /* Notification Preferences Content */
                            <div className="flex flex-col h-full">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Notification Preferences</h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Choose which notifications you want to receive</p>
                                    </div>
                                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                        <span className="mr-1">Home</span> / <span className="ml-1 font-semibold">Notifications</span>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {/* Job Card Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-clipboard-check text-success-500"></i>
                                            Job Card Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "jobCardAssigned" as const, label: "New Job Card Assigned", desc: "Get notified when a job card is assigned to you" },
                                                { key: "jobCardCompleted" as const, label: "Job Card Completed", desc: "Get notified when a job card is completed" },
                                                { key: "jobCardUpdated" as const, label: "Job Card Updated", desc: "Get notified when a job card is updated" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Inventory Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-box-seam text-warning-500"></i>
                                            Inventory Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "lowStockAlert" as const, label: "Low Stock Alert", desc: "Get notified when stock levels are low" },
                                                { key: "outOfStockAlert" as const, label: "Out of Stock Alert", desc: "Get notified when items are out of stock" },
                                                { key: "stockReceived" as const, label: "Stock Received", desc: "Get notified when new stock is received" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Payment Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-cash-coin text-success-500"></i>
                                            Payment Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "paymentReceived" as const, label: "Payment Received", desc: "Get notified when payments are received" },
                                                { key: "paymentPending" as const, label: "Payment Pending", desc: "Get notified about pending payments" },
                                                { key: "invoiceGenerated" as const, label: "Invoice Generated", desc: "Get notified when invoices are generated" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Vehicle Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-car-front text-info-500"></i>
                                            Vehicle Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "inspectionCompleted" as const, label: "Inspection Completed", desc: "Get notified when vehicle inspections are completed" },
                                                { key: "inspectionDue" as const, label: "Inspection Due", desc: "Get notified when inspections are due" },
                                                { key: "serviceReminder" as const, label: "Service Reminder", desc: "Get notified about upcoming service reminders" },
                                                { key: "vehicleAdded" as const, label: "Vehicle Added", desc: "Get notified when new vehicles are added" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Estimate Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-file-earmark-text text-primary-500"></i>
                                            Estimate Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "estimateCreated" as const, label: "Estimate Created", desc: "Get notified when estimates are created" },
                                                { key: "estimateApproved" as const, label: "Estimate Approved", desc: "Get notified when estimates are approved" },
                                                { key: "estimateRejected" as const, label: "Estimate Rejected", desc: "Get notified when estimates are rejected" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* QC Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-check-circle text-success-500"></i>
                                            Quality Control Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "qcApprovalRequired" as const, label: "QC Approval Required", desc: "Get notified when QC approval is required" },
                                                { key: "qcApproved" as const, label: "QC Approved", desc: "Get notified when QC is approved" },
                                                { key: "qcRejected" as const, label: "QC Rejected", desc: "Get notified when QC is rejected" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* System Notifications */}
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                                            <i className="bi bi-gear text-gray-500"></i>
                                            System Notifications
                                        </h3>
                                        <div className="space-y-2">
                                            {[
                                                { key: "systemUpdate" as const, label: "System Update", desc: "Get notified about system updates and new features" },
                                                { key: "maintenanceReminder" as const, label: "Maintenance Reminder", desc: "Get notified about scheduled maintenance" },
                                            ].map(({ key, label, desc }) => (
                                                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={notificationPrefs[key]}
                                                            onChange={() => handleNotificationToggle(key)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-success-500 dark:peer-focus:ring-success-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-success-500 dark:peer-checked:bg-success-500"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : activeTab === "views" && showNotificationPreferences ? (
                            /* View Preferences Content */
                            <div className="flex flex-col h-full">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">View Preferences</h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Manage your default view preferences for tables and cards</p>
                                    </div>
                                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                        <span className="mr-1">Home</span> / <span className="ml-1 font-semibold">View Preferences</span>
                                    </div>
                                </div>

                                {/* Submodule Edit/Delete Toggle */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50 mb-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                                                <i className="bi bi-list-ul text-primary-500"></i>
                                                Submodule Actions
                                            </h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Show or hide Edit and Delete buttons in submodule tables. This can be overridden per screen in configuration.
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer ml-4">
                                            <input
                                                type="checkbox"
                                                checked={submoduleShowEditDelete}
                                                onChange={(e) => setSubmoduleShowEditDelete(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-400 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-500 dark:peer-checked:bg-primary-500"></div>
                                        </label>
                                    </div>
                                </div>

                                {Object.keys(viewPrefs).length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <p className="text-sm">No view preferences saved yet.</p>
                                        <p className="text-xs mt-2">View preferences will be saved automatically when you change views in tables.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {Object.entries(viewPrefs).map(([tableId, prefs]) => (
                                            <div key={tableId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                                        <i className="bi bi-table text-primary-500"></i>
                                                        {tableId}
                                                    </h3>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* View Mode Selection */}
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            View Mode
                                                        </label>
                                                        <select
                                                            value={prefs.viewMode || "table"}
                                                            onChange={(e) => handleViewModeChange(tableId, e.target.value as "table" | "card")}
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                                                        >
                                                            <option value="table">Table</option>
                                                            <option value="card">Card</option>
                                                        </select>
                                                    </div>

                                                    {/* Table View Mode Selection */}
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Table View Mode
                                                        </label>
                                                        <select
                                                            value={prefs.tableViewMode || "standard"}
                                                            onChange={(e) => handleTableViewModeChange(tableId, e.target.value as "progress" | "standard")}
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                                                        >
                                                            <option value="standard">Standard</option>
                                                            <option value="progress">Progress</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {/* Submit Button Area */}
                        <div className="flex justify-end mt-4 space-x-3">
                            <button
                                className={`px-6 py-2 rounded-lg 
                                            bg-success-500 
                                            text-white font-medium 
                                            hover:bg-success-600 
                                            focus:bg-success-500 
                                            active:bg-success-600 
                                            focus:outline-none 
                                            focus:ring-0
                                            transition-all shadow-sm
                                            ${loading ? 'opacity-70 cursor-wait' : ''}
                                        `}
                                onClick={
                                    activeTab === "access" 
                                        ? handleAccessSubmit 
                                        : activeTab === "notifications"
                                        ? handleNotificationPreferencesSubmit
                                        : activeTab === "views"
                                        ? handleViewPreferencesSubmit
                                        : handleSubmit
                                }
                                disabled={loading}
                            >
                                {loading && activeTab === "access" 
                                    ? 'Saving Permissions...' 
                                    : loading && activeTab === "notifications"
                                    ? 'Saving Preferences...'
                                    : loading && activeTab === "views"
                                    ? 'Saving View Preferences...'
                                    : loading 
                                    ? 'Saving...' 
                                    : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>


            </div>
        </div>
    );
};
